import type { SearchParameterName, SearchPredicate } from '../searchParameters';
import {
  isImage,
  isFilm,
  isVideoTemplate,
  assetTypeIncludesImage,
  assetTypeIsImageFilmOrBoth,
  isCreativeOnly,
  isEditorialOnly,
  familyIncludesCreative,
  familyIncludesEditorial,
  isCreativeAndEditorial,
} from '../searchParameters';
// ---------------------------------------------------------------------------
// Search-parameter availability
// ---------------------------------------------------------------------------

// Whether a given search parameter (filter) applies to a completed search comes
// down to two independent concerns: the search's asset type and family (the
// asset-type / family traits in searchParameters/predicates), and the search mode
// (search-by-image, more-like-this, curated set, artist, event, contributor, …).
// The mode concern is owned by SearchMode, consulted via
// `availableSearchParametersFor`; the traits speak only to asset type and
// family so that each rule in ASSET_TYPE_AND_FAMILY_RULES reads as a single,
// intention-revealing line.

// --- Per-parameter rules that combine several asset-type / family conditions ---
const isMoodAvailable: SearchPredicate = (searchParameters) =>
  assetTypeIncludesImage(searchParameters) && isCreativeOnly(searchParameters);

const isLicenseAvailable: SearchPredicate = (searchParameters) =>
  (isImage(searchParameters) && isCreativeAndEditorial(searchParameters)) ||
  (isFilm(searchParameters) && familyIncludesCreative(searchParameters));

const isNumberOfPeopleAvailable: SearchPredicate = (searchParameters) =>
  assetTypeIncludesImage(searchParameters) ||
  (isFilm(searchParameters) && familyIncludesCreative(searchParameters));

// Each rule maps the parameters it governs to the condition under which they
// apply. The first rule whose `params` contains the requested parameter wins;
// an unlisted parameter is available by default.
export const ASSET_TYPE_AND_FAMILY_RULES: {
  params: SearchParameterName[];
  isAvailable: SearchPredicate;
}[] = [
  { params: ['locations', 'people'], isAvailable: assetTypeIsImageFilmOrBoth },
  {
    params: ['videotemplatecategory', 'videotemplateappversion'],
    isAvailable: isVideoTemplate,
  },
  {
    params: ['assetfiletype', 'alloweduse', 'color', '_colorindex'],
    isAvailable: (searchParameters) =>
      isCreativeOnly(searchParameters) &&
      assetTypeIncludesImage(searchParameters),
  },
  {
    params: ['phraseprocessing'],
    isAvailable: (searchParameters) =>
      (assetTypeIncludesImage(searchParameters) &&
        (isCreativeOnly(searchParameters) ||
          isEditorialOnly(searchParameters))) ||
      (isFilm(searchParameters) && familyIncludesCreative(searchParameters)),
  },
  { params: ['imagesize', 'embeddable'], isAvailable: assetTypeIncludesImage },
  // A creative search of images, of film, or of both. A site that offers the
  // filter more widely says so in its own configuration, in place of this rule.
  {
    params: ['excludenudity'],
    isAvailable: (searchParameters) =>
      isCreativeOnly(searchParameters) &&
      (assetTypeIncludesImage(searchParameters) || isFilm(searchParameters)),
  },
  {
    params: ['mediatype'],
    isAvailable: (searchParameters) =>
      assetTypeIncludesImage(searchParameters) &&
      familyIncludesCreative(searchParameters),
  },
  { params: ['mood'], isAvailable: isMoodAvailable },

  {
    params: [
      'videodurationmin',
      'videodurationmax',
      'filmdefinition',
      'framerate',
      'imagetechnique',
      'viewpoint',
    ],
    isAvailable: isFilm,
  },
  // ethnicity, ageofpeople and suppressfamilycorrection share one rule: a
  // creative-inclusive search over any photographic asset type.
  {
    params: ['ethnicity', 'ageofpeople', 'suppressfamilycorrection'],
    isAvailable: (searchParameters) =>
      familyIncludesCreative(searchParameters) &&
      assetTypeIsImageFilmOrBoth(searchParameters),
  },
  {
    params: ['specialcollectionid'],
    isAvailable: (searchParameters) =>
      familyIncludesCreative(searchParameters) && isImage(searchParameters),
  },
  {
    params: ['clarifications'],
    isAvailable: (searchParameters) =>
      isCreativeOnly(searchParameters) && isImage(searchParameters),
  },
  {
    params: ['editorialsources'],
    isAvailable: (searchParameters) =>
      assetTypeIncludesImage(searchParameters) &&
      familyIncludesEditorial(searchParameters),
  },
  {
    params: ['releasestatus'],
    isAvailable: (searchParameters) =>
      isFilm(searchParameters) && familyIncludesCreative(searchParameters),
  },
  {
    params: ['offlinecontent'],
    isAvailable: (searchParameters) =>
      isFilm(searchParameters) && familyIncludesEditorial(searchParameters),
  },
  {
    params: [
      'begindate',
      'enddate',
      'recency',
      'eventid',
      'parenteventid',
      'events',
    ],
    isAvailable: familyIncludesEditorial,
  },
  {
    params: ['editorialproducts', 'specificpeople', 'groupbyevent'],
    isAvailable: isEditorialOnly,
  },
  { params: ['license'], isAvailable: isLicenseAvailable },
  { params: ['numberofpeople'], isAvailable: isNumberOfPeopleAvailable },
];

// AvailableParameters#has
// Available if:
//   not excluded by the site
//   available in search mode
//   named in the site's availableOnlyIn: in one of the scopes listed there,
//     and nowhere else
//   not named there: available per the asset type and family rules above
